// =============================================================================
// hooks/admin/useFeatureFlags.ts — Queries/mutations TanStack Query para a
// administração de feature flags (F1-S23, estendido em F32-S04 com audience
// de organização/usuário).
//
// Fornece:
//   useAdminFeatureFlags()      → GET /api/admin/feature-flags
//   useUpdateFeatureFlag(opts)  → PATCH /api/admin/feature-flags/:key
//     (aceita status/visible/ui_label/description/audience — audience é
//      SUBSTITUÍDA por inteiro pelo backend, nunca mesclada por eixo; ver
//      apps/api/src/modules/featureFlags/repository.ts#updateFlag)
//
// Nunca useEffect+fetch. TanStack Query é o único caminho pra rede.
// Zod valida a resposta (padrão M1 — ver lib/api/cities.ts).
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { useToast } from '../../components/ui/Toast';
import { api } from '../../lib/api';
import { FEATURE_FLAGS_QUERY_KEY } from '../useFeatureFlag';

import type { FeatureFlagDto, PatchFeatureFlagBody } from './useFeatureFlags.types';

// ---------------------------------------------------------------------------
// Zod schemas — espelham featureFlagSchema / featureFlagAudienceSchema do
// backend (apps/api/src/modules/featureFlags/schemas.ts).
// ---------------------------------------------------------------------------

const FeatureFlagStatusSchema = z.enum(['enabled', 'disabled', 'internal_only']);

const AUDIENCE_AXIS_MAX = 200;

// `.default([])` em vez de `.optional()`: com exactOptionalPropertyTypes, um
// eixo ausente na resposta vira array vazio (semântica idêntica — eixo
// ausente ou vazio não restringe) em vez de `string[] | undefined`, que o
// TS rejeitaria ao atribuir para `FeatureFlagAudience` (roles?: string[]).
const FeatureFlagAudienceSchema = z.object({
  roles: z.array(z.string()).max(AUDIENCE_AXIS_MAX).default([]),
  city_ids: z.array(z.string().uuid()).max(AUDIENCE_AXIS_MAX).default([]),
  user_ids: z.array(z.string().uuid()).max(AUDIENCE_AXIS_MAX).default([]),
  organization_ids: z.array(z.string().uuid()).max(AUDIENCE_AXIS_MAX).default([]),
});

const FeatureFlagSchema = z.object({
  key: z.string(),
  status: FeatureFlagStatusSchema,
  visible: z.boolean(),
  ui_label: z.string().nullable(),
  description: z.string().nullable(),
  audience: FeatureFlagAudienceSchema,
  updated_by: z.string().uuid().nullable(),
  updated_at: z.string(),
  created_at: z.string(),
});

const FeatureFlagListSchema = z.array(FeatureFlagSchema);

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

async function apiListAdminFlags(): Promise<FeatureFlagDto[]> {
  const raw = await api.get('/api/admin/feature-flags');
  return FeatureFlagListSchema.parse(raw);
}

async function apiPatchFlag(key: string, body: PatchFeatureFlagBody): Promise<FeatureFlagDto> {
  const raw = await api.patch(`/api/admin/feature-flags/${encodeURIComponent(key)}`, body);
  return FeatureFlagSchema.parse(raw);
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const ADMIN_FEATURE_FLAGS_QUERY_KEY = ['feature-flags', 'admin', 'list'] as const;

// ---------------------------------------------------------------------------
// useAdminFeatureFlags — lista completa (admin)
// ---------------------------------------------------------------------------

export function useAdminFeatureFlags(): {
  data: FeatureFlagDto[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ADMIN_FEATURE_FLAGS_QUERY_KEY,
    queryFn: apiListAdminFlags,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return { data: data ?? [], isLoading, isError, refetch: () => void refetch() };
}

// ---------------------------------------------------------------------------
// useUpdateFeatureFlag — mutation PATCH genérica (toggle e/ou audience)
// ---------------------------------------------------------------------------

interface UseUpdateFeatureFlagOptions {
  onSuccess?: ((result: FeatureFlagDto) => void) | undefined;
  /** Se false, não dispara toast de sucesso genérico (o chamador cuida). */
  silent?: boolean | undefined;
}

export function useUpdateFeatureFlag(opts: UseUpdateFeatureFlagOptions = {}): {
  update: (key: string, body: PatchFeatureFlagBody) => void;
  updateAsync: (key: string, body: PatchFeatureFlagBody) => Promise<FeatureFlagDto>;
  isPending: boolean;
  pendingKey: string | null;
} {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: ({ key, body }: { key: string; body: PatchFeatureFlagBody }) =>
      apiPatchFlag(key, body),
    onSuccess: (result) => {
      // Invalida a lista admin e o mapa /me (cache client de 30s do próprio usuário).
      void queryClient.invalidateQueries({ queryKey: ADMIN_FEATURE_FLAGS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FEATURE_FLAGS_QUERY_KEY });
      if (opts.silent !== true) {
        toast('Feature flag atualizada.', 'success');
      }
      opts.onSuccess?.(result);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Erro ao atualizar feature flag.';
      toast(msg, 'danger');
    },
  });

  return {
    update: (key, body) => mutation.mutate({ key, body }),
    updateAsync: (key, body) => mutation.mutateAsync({ key, body }),
    isPending: mutation.isPending,
    pendingKey: mutation.isPending ? (mutation.variables?.key ?? null) : null,
  };
}
